'use client';

import React, { useEffect, useRef, useState } from 'react';


interface TourStep {
  element: string;
  title: string;
  description: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
}

const TOUR_STEPS: TourStep[] = [
  {
    element: '[aria-label="Navigazione principale"]',
    title: '👋 Benvenuto in LabCallManager!',
    description: 'Questa è la sidebar di navigazione. Clicca sui gruppi per espanderli e accedere alle sezioni.',
    side: 'right',
  },
  {
    element: '[aria-label="Apri ricerca globale (Ctrl+K)"]',
    title: '🔍 Ricerca Globale',
    description: 'Usa Ctrl+K per cercare pazienti, chiamate e pagine da qualsiasi punto dell\'app.',
    side: 'bottom',
  },
  {
    element: '[aria-label="Passa alla modalità scura"], [aria-label="Passa alla modalità chiara"]',
    title: '🌙 Dark Mode',
    description: 'Alterna tra modalità chiara e scura. La preferenza viene salvata automaticamente.',
    side: 'bottom',
  },
];

const TOUR_KEY = 'lcm_tour_completed';

interface OnboardingTourProps {
  autoStart?: boolean;
}

export default function OnboardingTour({ autoStart = false }: OnboardingTourProps) {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const [highlightStyle, setHighlightStyle] = useState<React.CSSProperties>({});
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoStart && typeof window !== 'undefined') {
      const completed = localStorage.getItem(TOUR_KEY);
      if (!completed) {
        setTimeout(() => setActive(true), 1000);
      }
    }
  }, [autoStart]);

  useEffect(() => {
    if (!active) return;
    positionStep(step);
  }, [active, step]);

  const positionStep = (stepIndex: number) => {
    const tourStep = TOUR_STEPS[stepIndex];
    if (!tourStep) return;

    const el = document.querySelector(tourStep.element) as HTMLElement;
    if (!el) {
      // Skip to next if element not found
      if (stepIndex < TOUR_STEPS.length - 1) setStep(s => s + 1);
      return;
    }

    const rect = el.getBoundingClientRect();
    const padding = 8;

    setHighlightStyle({
      position: 'fixed',
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
      borderRadius: 8,
      boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
      zIndex: 9990,
      pointerEvents: 'none',
    });

    const popoverWidth = 300;
    const side = tourStep.side ?? 'bottom';
    let top = 0;
    let left = 0;

    if (side === 'bottom') {
      top = rect.bottom + padding + 8;
      left = rect.left + rect.width / 2 - popoverWidth / 2;
    } else if (side === 'top') {
      top = rect.top - padding - 8 - 120;
      left = rect.left + rect.width / 2 - popoverWidth / 2;
    } else if (side === 'right') {
      top = rect.top + rect.height / 2 - 60;
      left = rect.right + padding + 8;
    } else if (side === 'left') {
      top = rect.top + rect.height / 2 - 60;
      left = rect.left - popoverWidth - padding - 8;
    }

    // Clamp to viewport
    left = Math.max(16, Math.min(left, window.innerWidth - popoverWidth - 16));
    top = Math.max(16, Math.min(top, window.innerHeight - 160));

    setPopoverStyle({
      position: 'fixed',
      top,
      left,
      width: popoverWidth,
      zIndex: 9995,
    });
  };

  const handleNext = () => {
    if (step < TOUR_STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      completeTour();
    }
  };

  const handlePrev = () => {
    if (step > 0) setStep(s => s - 1);
  };

  const completeTour = () => {
    setActive(false);
    localStorage.setItem(TOUR_KEY, 'true');
  };

  if (!active) return null;

  const currentStep = TOUR_STEPS[step];

  return (
    <>
      {/* Highlight overlay */}
      <div style={highlightStyle} aria-hidden="true" />

      {/* Popover */}
      <div
        style={popoverStyle}
        role="dialog"
        aria-modal="true"
        aria-label={`Tour step ${step + 1} di ${TOUR_STEPS.length}: ${currentStep.title}`}
        className="bg-white dark:bg-card border border-border rounded-xl shadow-2xl p-4 animate-fade-in"
      >
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-sm font-semibold text-foreground">{currentStep.title}</h3>
          <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
            {step + 1}/{TOUR_STEPS.length}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{currentStep.description}</p>

        {/* Progress dots */}
        <div className="flex items-center gap-1 mb-3">
          {TOUR_STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? 'w-4 bg-primary' : i < step ? 'w-1.5 bg-primary/40' : 'w-1.5 bg-muted'
              }`}
            />
          ))}
        </div>

        <div className="flex items-center gap-2">
          {step > 0 && (
            <button
              onClick={handlePrev}
              className="px-3 py-1.5 border border-border rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
            >
              ← Indietro
            </button>
          )}
          <button
            onClick={completeTour}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto"
          >
            Salta tour
          </button>
          <button
            onClick={handleNext}
            className="px-3 py-1.5 bg-primary-dark text-white rounded-lg text-xs font-medium hover:opacity-90 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {step < TOUR_STEPS.length - 1 ? 'Avanti →' : '✓ Fatto!'}
          </button>
        </div>
      </div>
    </>
  );
}

export { TOUR_KEY };
